<template>
  <k-modal v-if="isReady" id="add-new-scope-modal" :show="showModal"
    @close="onClose"
    :closeOnClickAway="false"
  >
    <template #header>
      <h3>{{ modalTitle }}</h3>
    </template>
    <template #body>
      <div class="modal-body">
        <fieldset class="detail-form form-entry">
          <label for="project-name-input">Project Name <span class="form-required">*</span>
            <input id="project-name-input" class="form-control" :readonly="editingDisabled" placeholder="Type Project name here..." v-model="projectName">
          </label>
        </fieldset>
        <fieldset class="detail-form form-entry ">
          <label for="project-scoping-input">Add Scoping Document</label>
          <div class="scope-upload-container">
            <div v-if="displayScopeLink" class="scope-link-view">
              <div class="scope-link-container">
                <a :href="projectScopingDocument" target="_blank" class="scope-link">{{ projectScopingDocument }}</a>
              </div>
              <button class="btn btn-outlined" @click="editScopeLink" title="Edit scoping document link" aria-label="Edit scoping document link">
              <i class="fas fa-pencil-alt"></i>
              </button>
            </div>
            <div v-else class="scope-link-edit">
                <input class="form-control" :readonly="editingDisabled" placeholder="Add scoping document link here..." v-model="pendingProjectScopingDocument">
                <button :disabled="!pendingProjectScopingDocument" class="btn btn-success" @click="confirmScopeLink" title="Save scoping document link" aria-label="Save scoping document link">
                  <i class="fas fa-check"></i>
                </button>
            </div>
            <span>OR</span>
            <div class="scope-upload">
              <dropfield @file="filesChange"
                ref="scopeUploadField"
                :immediatePost="false"
                :disableDropfield="editingDisabled || isUploading"
                accept="application/pdf"
                url=""
                :enablePreviews="true"
                id="upload-scope-document"
              >
              </dropfield>
            </div>
          </div>
        </fieldset>
        <fieldset class="form-group description-container form-required">
          <label for="project-description-input">
            Project Description
            <span class="character-count" :class="{ 'near-limit': projectDescription.length > descMaxLength * 0.9 }">
              {{ projectDescription.length }}/{{ descMaxLength }} characters
            </span>
          </label>
          <textarea
              id="project-description-input"
              placeholder="Type description here..."
              v-model="projectDescription"
              rows="3"
              :maxlength="descMaxLength"
              :readonly="editingDisabled"/>
        </fieldset>
        <fieldset class="detail-form form-entry competency-container">
          <label class="label-text">Select Competencies relevant to the project <span class="form-required">*</span></label>
          <template v-if="competenciesReady && competencies.length > 0">
            <k-editable-table
              v-model="competencies"
              :headers="tableHeaders"
              :max="10"
            />
          </template>
          <div v-else>
            <span class="label-text">No competencies found for this programme</span>
          </div>
        </fieldset>
      </div>
    </template>
    <template #footer>
      <div class="modal-footer-left">
        <button class="btn btn-outlined" @click="$emit('close')" aria-label='Cancel'>Cancel</button>
      </div>
      <div class="modal-footer-right">
        <button v-if="modalMode === 'edit'" class="btn btn-danger delete-draft" aria-label='Delete draft' @click="confirmDeleteDraft" :disabled="editingDisabled">Delete Draft</button>
        <button class="btn btn-default save-draft" @click="saveOrEditDraft" aria-label='Save as draft' :disabled="!validSaveAsDraft || editingDisabled">Save as Draft</button>
        <button class="btn btn-success submit" @click="submitForReview" aria-label='Submit for review' :disabled="!validSubmitForReview || editingDisabled">Submit for Review</button>
      </div>
    </template>
  </k-modal>
</template>

<style>
#add-new-scope-modal .modal-footer {
  display: flex;
  justify-content: space-between;
}

#add-new-scope-modal .modal-container {
  width: 80vw;
}

#add-new-scope-modal .form-control[readonly],
#add-new-scope-modal textarea[readonly] {
  background-color: var(--input-background);
  border: var(--input-border);
  cursor: not-allowed;
}
</style>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.description-container {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
}

.scope-upload-container {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 40px;
}

.scope-upload {
  display: flex;
  width: 50%;
  gap: 20px;
  align-items: center;
}

.scope-link-view,
.scope-link-edit {
  display: flex;
  width: 50%;
  gap: 10px;
  align-items: center;
}

.scope-link {
  display: inline;
  color: var(--kate-type-link);
  text-decoration: underline;
  font-size: 0.9em;
  cursor: pointer;
}

.scope-link:hover {
  color: var(--kate-type-link-hover);
}

.scope-link-container {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--input-border);
  border-radius: 4px;
  background-color: var(--input-background);
  font-family: inherit;
  line-height: 1.5;
  min-height: 38px;
  display: flex;
  align-items: center;
}

#upload-scope-document {
  width: 100%;
}

.scope-upload-container :deep(.k-dropbox) {
  display: flex;
  min-height: 50px;
  height: 50px;
}

.scope-upload-container :deep(.k-dropbox) > label {
  display: block;
  padding: 0;
  font-size: 1em;
}

.description-container > label {
  width: 100%;
}

.detail-form > label {
  width: 100%;
  display: block;
}

.character-count {
  float: right;
  font-size: 0.8em;
  color: var(--kate-type-secondary);
  margin-top: 4px;
}

.character-count.near-limit {
  color: var(--kate-type-danger);
}

.description-container textarea {
  width: 100%;
  min-height: 80px;
  padding: 10px;
  border: var(--input-border-alt);
  border-radius: 4px;
  resize: vertical;
}

.competency-container {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

</style>

<script>
import KModal from '@base-components/k-modal.vue';
import KEditableTable from '@base-components/k-editable-table.vue';
import Dropfield from '@base-components/dropfield.vue';

const DESC_MAX_LENGTH = 2048;

export default {
  components: {
    KModal,
    KEditableTable,
    Dropfield,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },

    programmeId: {
      type: Number,
      required: true,
    },
    modalMode: {
      type: String,
      required: true,
    },
    scopeData: {
      type: Object,
      default: undefined,
    },
  },

  data() {
    return {
      isUploading: false,
      isUpdating: false,
      projectName: '',
      projectDescription: '',
      projectScopingDocument: null,
      competencies: [],
      competenciesReady: false,
      isReady: true,
      descMaxLength: DESC_MAX_LENGTH,
      displayScopeLink: false,
      pendingProjectScopingDocument: null,
    };
  },

  watch: {
    showModal(newVal) {
      if (newVal) {
        this.getCompetencies();
      }
    },

    scopeData: {
      handler(newVal) {
        if (newVal) {
          this.projectName = newVal.name || '';
          this.projectDescription = newVal.description || '';
          this.projectScopingDocument = newVal.scoping_document_link || null;
          this.draftCompetencies = newVal.competencies || [];

          // Set display state for existing links
          this.displayScopeLink = !!newVal.scoping_document_link;
          this.pendingProjectScopingDocument = null;
        } else {
          // Reset form when no project scope data is provided
          this.clearFields();
        }
      },
      immediate: true,
    },
  },

  computed: {
    modalTitle() {
      if (this.modalMode === 'view' && this.scopeData) {
        return `View ${this.scopeData.name}`;
      }
      return this.modalMode === 'edit' && this.scopeData ? `Edit ${this.scopeData.name}` : 'Scope New Project';
    },

    validSubmitForReview() {
      return Boolean(this.validName && this.validDescription && this.validCompetencies && !this.scopeData?.scoping_completed);
    },

    validSaveAsDraft() {
      return Boolean(this.validName && !this.scopeData?.scoping_completed);
    },

    editingDisabled() {
      return this.modalMode === 'view' || this.scopeData?.scoping_completed || this.isUpdating;
    },

    validName() {
      return this.projectName.trim() !== '';
    },

    validDescription() {
      return this.projectDescription.trim() !== '';
    },

    validCompetencies() {
      return this.listSelectedCompetencies().length > 0;
    },

    tableHeaders() {
      return [
        {
          key: 'checkbox',
          title: '',
          editable: !this.editingDisabled,
          type: 'boolean',
          required: true,
          sortable: false,
        },
        {
          key: 'label',
          title: 'Label',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
        {
          key: 'category',
          title: 'Category',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
        {
          key: 'description',
          title: 'Description',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
      ];
    },
  },

  methods: {
    getCompetencies() {
      this.isReady = false;
      this.$Loading.minimal();
      this.$logger.info('Fetching competencies', true);
      this.$http.get(`/api/curriculum/programmes/${this.programmeId}/competencies/scores`)
        .then(response => {
          this.competencies = response.data.competencies;
          this.competenciesReady = true;

          // If we have scope data with competencies, mark the selected ones
          if (this.scopeData && this.scopeData.competencies) {
            this.markSelectedCompetencies(this.scopeData.competencies);
          }
        })
        .catch(error => {
          this.$logger.error('Error fetching competencies', error);
          this.competenciesReady = true;
        }).then(() => {
          this.isReady = true;
          this.$Loading.finish();
        });
    },

    filesChange(file) {
      // TODO: file upload not implemented yet
      this.$logger.info('Staged file for upload', { file: file.name });
      this.projectScopingDocument = file;
    },

    markSelectedCompetencies(draftCompetencies) {
      if (!this.competencies || !this.competencies.length) {
        return;
      }

      // Get the IDs of competencies from the scope data
      const selectedIds = draftCompetencies.map(c => c.id);

      // Create a completely new array to ensure Vue reactivity
      const updatedCompetencies = [];

      // Add each competency with the checkbox property set
      this.competencies.forEach(competency => {
        updatedCompetencies.push({
          ...competency,
          checkbox: selectedIds.includes(competency.id),
        });
      });

      // Replace the entire array to trigger reactivity
      this.competencies = updatedCompetencies;
    },

    listSelectedCompetencies() {
      return this.competencies.filter(competency => competency.checkbox);
    },

    deleteDraft() {
      this.isUpdating = true;
      this.$logger.info('Deleting project scope', { projectId: this.scopeData.id });
      this.$http.delete(`/api/curriculum/programmes/projects/${this.scopeData.id}`, {
        data: {
          programme_id: this.programmeId,
        },
      })
        .then(() => {
          this.$ktoast.success('Project scope deleted');
          this.$logger.info('Project scope deleted', { projectId: this.scopeData.id });
          this.$emit('close');
          this.$emit('update');
          this.clearFields();
        }).catch(err => {
          this.$logger.autowarn('Error deleting project scope', { projectId: this.scopeData.id }, err);
          this.$ktoast.warning('Error deleting project scope');
        }).finally(() => {
          this.isUpdating = false;
        });
    },

    saveNewProject(status) {
      this.isUpdating = true;
      const payload = {
        programme_id: this.programmeId,
        programme_competency_ids: this.listSelectedCompetencies().map(competency => competency.id),
        name: this.projectName,
        description: this.projectDescription,
        scoping_document_link: this.projectScopingDocument,
        project_type: 'portfolio',
        status,
      };
      this.$logger.info('Saving project scope', { status });
      this.$http.post('/api/curriculum/programmes/projects', payload)
        .then(() => {
          this.$ktoast.success(status === 'draft' ? 'Draft project scope saved' : 'Project scope submitted for review');
          this.$logger.info('Project scope saved', { status });
          this.$emit('close');
          this.$emit('update');
          this.clearFields();
        }).catch(err => {
          this.$logger.autowarn('Error saving project scope', { status }, err);
          this.$ktoast.warning(status === 'draft' ? 'Error saving draft project scope' : 'Error submitting project scope for review');
        }).finally(() => {
          this.isUpdating = false;
        });
    },

    editProject(status) {
      this.isUpdating = true;
      const payload = {
        programme_id: this.programmeId,
        programme_competency_ids: this.listSelectedCompetencies().map(competency => competency.id),
        name: this.projectName,
        description: this.projectDescription,
        scoping_document_link: this.projectScopingDocument,
        project_type: 'portfolio',
        status,
      };
      this.$logger.info('Updating project scope', { projectId: this.scopeData.id, status });
      this.$http.put(`/api/curriculum/programmes/projects/${this.scopeData.id}`, payload)
        .then(() => {
          this.$ktoast.success(status === 'draft' ? 'Draft project scope updated' : 'Project scope submitted for review');
          this.$logger.info('Project scope updated', { projectId: this.scopeData.id, status });
          this.$emit('close');
          this.$emit('update');
          this.clearFields();
        }).catch(err => {
          this.$logger.autowarn('Error saving project scope', { projectId: this.scopeData.id, status }, err);
          this.$ktoast.warning(status === 'draft' ? 'Error updating draft project scope' : 'Error submitting project scope for review');
        }).finally(() => {
          this.isUpdating = false;
        });
    },

    confirmDeleteDraft() {
      this.$ktoast.confirmToast(`Are you sure you want to delete this project: <strong>${this.scopeData.name}</strong>?`, 'warning', () => {
        this.deleteDraft();
      });
    },

    saveOrEditDraft() {
      if (this.modalMode === 'edit') {
        this.editProject('draft');
      } else {
        this.saveNewProject('draft');
      }
    },

    submitForReview() {
      if (this.modalMode === 'edit') {
        this.editProject('scoping_proposed');
      } else {
        this.saveNewProject('scoping_proposed');
      }
    },

    clearFields() {
      this.projectName = '';
      this.projectDescription = '';
      this.projectScopingDocument = null;
      this.displayScopeLink = false;
      this.pendingProjectScopingDocument = null;

      // Reset checkbox state for all competencies
      if (this.competencies && this.competencies.length > 0) {
        // Create a completely new array to ensure Vue reactivity
        const updatedCompetencies = [];

        // Add each competency with checkbox set to false
        this.competencies.forEach(competency => {
          updatedCompetencies.push({
            ...competency,
            checkbox: false,
          });
        });

        // Replace the entire array to trigger reactivity
        this.competencies = updatedCompetencies;
      }
    },

    confirmScopeLink() {
      this.projectScopingDocument = this.pendingProjectScopingDocument;
      this.pendingProjectScopingDocument = null;
      this.displayScopeLink = true;
    },

    editScopeLink() {
      this.displayScopeLink = false;
      this.pendingProjectScopingDocument = this.projectScopingDocument;
    },

    onClose() {
      this.clearFields();
      this.$emit('close');
    },
  },
};

</script>
